// docs: https://github.com/altostra/type-validations#readme
import { anyOf, arrayOf, enumOf, is, maybe, objectOf, withRecursion } from "@altostra/type-validations";
import { boolean, string } from "@altostra/type-validations/lib/primitives";

import type {
  TemplateComponentHeaderFormatText,
  TemplateComponentHeaderFormatMedia,
  TemplateComponentButtonTypeUrl,
  TemplateComponentButtonTypePhoneNumber,
  TemplateComponentButtonTypeOther,
} from "./types";

const isTemplateComponentHeaderFormatText = is<TemplateComponentHeaderFormatText>("text");
const isTemplateComponentHeaderText = objectOf({
  header_format: isTemplateComponentHeaderFormatText,
  text: string,
  examples: maybe(arrayOf(string)),
});

const isTemplateComponentHeaderFormatMedia = enumOf<TemplateComponentHeaderFormatMedia>(
  "image",
  "video",
  "document"
);
const isTemplateComponentHeaderMedia = objectOf({
  header_format: isTemplateComponentHeaderFormatMedia,
  text: string,
  examples: maybe(arrayOf(string)),
});

const isTemplateComponentHeader = anyOf(isTemplateComponentHeaderText, isTemplateComponentHeaderMedia);

const isTemplateComponentBody = objectOf({
  text: string,
  examples: maybe(arrayOf(string)),
});

const isTemplateComponentFooter = objectOf({
  text: string,
});

const isTemplateComponentButtonTypeUrl = is<TemplateComponentButtonTypeUrl>("url");
const isTemplateComponentButtonUrl = objectOf({
  button_type: isTemplateComponentButtonTypeUrl,
  url: string,
  examples: maybe(arrayOf(string)),
});

const isTemplateComponentButtonTypePhoneNumber = is<TemplateComponentButtonTypePhoneNumber>("phone-number");
const isTemplateComponentButtonPhoneNumber = objectOf({
  button_type: isTemplateComponentButtonTypePhoneNumber,
  phone_number: string,
  examples: maybe(arrayOf(string)),
});

const isTemplateComponentButtonTypeOther = enumOf<TemplateComponentButtonTypeOther>("quick-reply", "copy-code");
const isTemplateComponentButtonOther = objectOf({
  button_type: isTemplateComponentButtonTypeOther,
  text: string,
  examples: maybe(arrayOf(string)),
});

const isTemplateComponentButton = anyOf(
  isTemplateComponentButtonUrl,
  isTemplateComponentButtonPhoneNumber,
  isTemplateComponentButtonOther
);

const isTemplateComponents = withRecursion((isTemplateComponents) =>
  objectOf({
    header: maybe(isTemplateComponentHeader),
    body: maybe(isTemplateComponentBody),
    footer: maybe(isTemplateComponentFooter),
    buttons: maybe(arrayOf(isTemplateComponentButton)),
    carousel_cards: maybe(arrayOf(isTemplateComponents)),
  })
);

const isTemplatePrefillContent = withRecursion((isTemplatePrefillContent) =>
  objectOf({
    header_media_type: maybe(isTemplateComponentHeaderFormatMedia),
    header_media_uri: maybe(string),
    header_asset_id: maybe(string),
    header_liquid_variables: maybe(arrayOf(string)),
    body_liquid_variables: maybe(arrayOf(string)),
    button_liquid_variables: maybe(arrayOf(string)),
    carousel_cards: maybe(arrayOf(isTemplatePrefillContent)),
  })
);

export const isPostWhatsAppMessageTemplateRequest = objectOf({
  external_account_ids: arrayOf(string),
  template: objectOf({
    name: string,
    language: string,
    category: string,
    subtype: string,
    components: maybe(isTemplateComponents),
    prefill_content: maybe(isTemplatePrefillContent),
    click_tracking_enabled: maybe(boolean),
    custom_domain: maybe(string),
  }),
});

const isSuccessResponse = objectOf({
  success: is(true),
  external_account_id: maybe(string),
});

const isErrorResponse = objectOf({
  success: is(false),
  external_account_id: maybe(string),
  error: string,
  meta_api_response: string,
});

export const isPostWhatsAppMessageTemplateResponse = objectOf({
  overall_success: boolean,
  results: arrayOf(anyOf(isSuccessResponse, isErrorResponse)),
});
